import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { Link } from '@tanstack/react-router'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { buildUtilities, getUtilityDefinition, type UtilityKey } from '@/shared/utilities/utilities'
import {
  PDF_VIEWER_MAX_FILE_BYTES,
  PDF_VIEWER_MAX_PAGES,
  PdfViewerError,
  openPdfViewer,
  validatePdfViewerFile,
  type PdfViewerSession,
} from '@/features/pdf-viewer/model/pdfViewer'

type ViewerState =
  | { kind: 'idle' }
  | { kind: 'loading'; file: File }
  | { kind: 'ready'; file: File; session: PdfViewerSession }
  | { kind: 'error'; file: File | null; code: string }

interface PdfViewerProps {
  openOverride?: typeof openPdfViewer
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
const RELATED_KEYS = [
  'povernut',
  'splitPdf',
  'szhat',
  'pdfToText',
  'vJpg',
  'pdf-to-png',
  'pdf-metadata',
] as const satisfies readonly UtilityKey[]

const definition = getUtilityDefinition('pdf-viewer')
const analytics = {
  app: 'pdfy',
  domain: 'pdfy.ru',
  route: definition.path,
  utility: definition.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

export function PdfViewer({ openOverride = openPdfViewer }: PdfViewerProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  const inputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const loadAbortRef = useRef<AbortController | null>(null)
  const renderAbortRef = useRef<AbortController | null>(null)
  const sessionRef = useRef<PdfViewerSession | null>(null)
  const operationRef = useRef<ReturnType<typeof startToolOperation> | null>(null)
  const operationStartedAtRef = useRef(0)
  const generationRef = useRef(0)
  const [state, setState] = useState<ViewerState>({ kind: 'idle' })
  const [pageNumber, setPageNumber] = useState(1)
  const [zoomIndex, setZoomIndex] = useState(2)
  const [fit, setFit] = useState(true)
  const [dropActive, setDropActive] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  const [displayedPage, setDisplayedPage] = useState<number | null>(null)

  const closeActive = useCallback(() => {
    generationRef.current += 1
    loadAbortRef.current?.abort()
    renderAbortRef.current?.abort()
    loadAbortRef.current = null
    renderAbortRef.current = null
    operationRef.current = null
    void sessionRef.current?.close()
    sessionRef.current = null
  }, [])

  useEffect(() => () => {
    generationRef.current += 1
    loadAbortRef.current?.abort()
    renderAbortRef.current?.abort()
    operationRef.current = null
    void sessionRef.current?.close()
    sessionRef.current = null
  }, [])

  useEffect(() => {
    if (state.kind !== 'ready') return undefined
    const canvas = canvasRef.current
    if (!canvas) return undefined

    renderAbortRef.current?.abort()
    const controller = new AbortController()
    renderAbortRef.current = controller
    const generation = generationRef.current
    setRendering(true)
    setRenderError(null)
    setDisplayedPage(null)
    canvas.width = 0
    canvas.height = 0
    const scale = fit ? 1.25 : ZOOM_LEVELS[zoomIndex] ?? 1

    state.session.renderPage(pageNumber, scale, canvas, controller.signal)
      .then(() => {
        if (controller.signal.aborted || generation !== generationRef.current) return
        operationRef.current?.success(performance.now() - operationStartedAtRef.current)
        operationRef.current = null
        setDisplayedPage(pageNumber)
        setRendering(false)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || generation !== generationRef.current) return
        const code = errorCode(error)
        if (operationRef.current || code === 'timeout') {
          operationRef.current?.error(analyticsError(code))
          operationRef.current = null
          sessionRef.current = null
          void state.session.close()
          setState({ kind: 'error', file: state.file, code })
        } else {
          setRendering(false)
          setRenderError(code)
        }
      })

    return () => controller.abort()
  }, [fit, pageNumber, state, zoomIndex])

  const reset = useCallback(() => {
    closeActive()
    setState({ kind: 'idle' })
    setPageNumber(1)
    setZoomIndex(2)
    setFit(true)
    setDropActive(false)
    setRendering(false)
    setRenderError(null)
    setDisplayedPage(null)
  }, [closeActive])

  const openFile = useCallback(async (file: File) => {
    closeActive()
    const generation = generationRef.current
    setPageNumber(1)
    setZoomIndex(2)
    setFit(true)
    setRenderError(null)
    setDisplayedPage(null)

    const validationError = validatePdfViewerFile(file)
    if (validationError) {
      trackToolRejection(analytics, analyticsError(validationError.code))
      setState({ kind: 'error', file, code: validationError.code })
      return
    }

    const controller = new AbortController()
    loadAbortRef.current = controller
    setState({ kind: 'loading', file })
    const operation = startToolOperation(analytics)
    operationRef.current = operation
    operationStartedAtRef.current = performance.now()
    try {
      const session = await openOverride(file, controller.signal)
      if (controller.signal.aborted || generation !== generationRef.current) {
        await session.close()
        return
      }
      loadAbortRef.current = null
      sessionRef.current = session
      setState({ kind: 'ready', file, session })
    } catch (error) {
      if (controller.signal.aborted || generation !== generationRef.current) return
      loadAbortRef.current = null
      const code = errorCode(error)
      operation.error(analyticsError(code))
      operationRef.current = null
      setState({ kind: 'error', file, code })
    }
  }, [closeActive, openOverride])

  function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) void openFile(file)
  }

  function dropFile(event: DragEvent) {
    event.preventDefault()
    setDropActive(false)
    const file = event.dataTransfer.files[0]
    if (file) void openFile(file)
  }

  const pageCount = state.kind === 'ready' ? state.session.pageCount : 0
  const copy = t.pdfViewer
  const utilityByKey = new Map(buildUtilities(t).map((utility) => [utility.key, utility]))

  return (
    <div className="space-y-6">
      <Card
        className={cn('transition-colors', dropActive && 'border-primary bg-primary/5')}
        onDragOver={(event) => {
          event.preventDefault()
          if (state.kind !== 'loading') setDropActive(true)
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={dropFile}
      >
        <CardContent className="p-5 sm:p-6">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            disabled={state.kind === 'loading'}
            onChange={selectFile}
          />

          {state.kind === 'idle' && (
            <button
              type="button"
              className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors hover:border-primary/50 hover:bg-accent/30"
              onClick={() => inputRef.current?.click()}
            >
              <span aria-hidden className="text-4xl">PDF</span>
              <span className="font-medium">{copy.pickFile}</span>
              <span className="text-sm text-muted-foreground">
                {format(copy.limit, {
                  size: PDF_VIEWER_MAX_FILE_BYTES / 1024 / 1024,
                  pages: PDF_VIEWER_MAX_PAGES,
                })}
              </span>
              <span className="text-xs text-muted-foreground">{copy.localNotice}</span>
            </button>
          )}

          {state.kind === 'loading' && (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
              <span className="text-3xl" aria-hidden>◌</span>
              <p className="font-medium">{copy.opening}</p>
              <p className="max-w-md truncate text-sm text-muted-foreground">{state.file.name}</p>
              <Button type="button" variant="outline" onClick={reset}>{copy.reset}</Button>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="flex min-h-64 flex-col items-center justify-center gap-4 text-center" role="alert">
              <span className="text-3xl" aria-hidden>!</span>
              <div>
                <h2 className="font-semibold">{copy.errorTitle}</h2>
                <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                  {copy.errors[state.code as keyof typeof copy.errors] ?? copy.errors.corrupt}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <Button type="button" onClick={() => inputRef.current?.click()}>{copy.chooseAnother}</Button>
                <Button type="button" variant="outline" onClick={reset}>{copy.reset}</Button>
              </div>
            </div>
          )}

          {state.kind === 'ready' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold">{copy.previewTitle}</h2>
                  <p className="truncate text-sm text-muted-foreground">{state.file.name}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={fit ? 'default' : 'outline'}
                    onClick={() => setFit(true)}
                  >
                    {copy.fit}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={!fit && zoomIndex === 2 ? 'default' : 'outline'}
                    onClick={() => { setFit(false); setZoomIndex(2) }}
                  >
                    100%
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={fit || zoomIndex === 0}
                    aria-label={copy.zoomOut}
                    onClick={() => setZoomIndex((value) => Math.max(0, value - 1))}
                  >
                    −
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={fit || zoomIndex === ZOOM_LEVELS.length - 1}
                    aria-label={copy.zoomIn}
                    onClick={() => setZoomIndex((value) => Math.min(ZOOM_LEVELS.length - 1, value + 1))}
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="relative max-h-[72vh] min-h-72 overflow-auto rounded-lg border bg-muted p-3">
                {rendering && (
                  <div className="absolute inset-x-0 top-3 z-10 mx-auto w-fit rounded-md bg-background/90 px-3 py-1 text-xs shadow">
                    {copy.rendering}
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  aria-label={format(copy.pageAlt, { page: displayedPage ?? pageNumber })}
                  aria-hidden={displayedPage !== pageNumber}
                  className={cn(
                    'mx-auto block bg-white shadow-sm',
                    displayedPage !== pageNumber && 'invisible',
                    fit ? 'h-auto max-w-full' : 'max-w-none',
                  )}
                />
              </div>

              {renderError && (
                <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  {copy.errors[renderError as keyof typeof copy.errors] ?? copy.errors.render_failed}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={pageNumber <= 1}
                    onClick={() => setPageNumber((page) => Math.max(1, page - 1))}
                  >
                    {copy.previous}
                  </Button>
                  <span className="min-w-28 text-center text-sm font-medium">
                    {format(copy.pageOf, { page: pageNumber, pages: pageCount })}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={pageNumber >= pageCount}
                    onClick={() => setPageNumber((page) => Math.min(pageCount, page + 1))}
                  >
                    {copy.next}
                  </Button>
                </div>
                <Button type="button" variant="outline" onClick={reset}>{copy.reset}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {state.kind === 'ready' && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <Card>
            <CardContent className="p-5">
              <h2 className="font-semibold">{copy.actionsTitle}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{copy.actionsDescription}</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {RELATED_KEYS.map((key) => {
                  const utility = utilityByKey.get(key)
                  if (!utility) return null
                  return (
                    <Link
                      key={key}
                      to={utility.path}
                      className="rounded-md border bg-background px-3 py-2 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      {utility.title} →
                    </Link>
                  )
                })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3 p-5">
              <h2 className="font-semibold">{copy.fileInfo}</h2>
              <dl className="space-y-3">
                <Fact label={copy.fileName} value={state.file.name} />
                <Fact label={copy.fileSize} value={formatBytes(state.file.size)} />
                <Fact label={copy.pageCount} value={String(pageCount)} />
              </dl>
              <p className="pt-2 text-xs leading-5 text-muted-foreground">{copy.localNotice}</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm font-medium">{value}</dd>
    </div>
  )
}

function errorCode(error: unknown): string {
  if (error instanceof PdfViewerError) return error.code
  return 'render_failed'
}

function analyticsError(code: string): ToolErrorCode {
  if (code === 'too_large' || code === 'too_many_pages' || code === 'page_too_large') return 'size_limit'
  if (code === 'unsupported' || code === 'encrypted') return 'unsupported_input'
  if (code === 'empty' || code === 'corrupt' || code === 'no_pages') return 'invalid_input'
  if (code === 'timeout') return 'timeout'
  return 'processing_failed'
}
